import { PrimerJSLogger } from '@primer-io/sdk-utils/PrimerJSLogger';
import {
  getBrowserInfo,
  isInAppBrowser,
  isMobile,
} from '@primer-io/sdk-utils/browserInfo';
import { delay } from '@primer-io/sdk-utils/delay';
import { loadStateProcessorModule } from './StateProcessorLoader';
import type {
  Client,
  ClientCapabilities,
  ClientRequirements,
  ClientRequirementsCheck,
  SDKContext,
  StateProcessorConfig,
  StateProcessorFactory,
  StepType,
  UnmetClientRequirement,
} from './types';

/** A step handler's statics: what it runs, at one version of its contract. */
export interface VersionedHandler {
  type: StepType;
  version: string;
}

export function capabilitiesOf(
  steps: readonly VersionedHandler[],
): ClientCapabilities {
  return {
    stepTypes: versionsOf(steps),
    uiComponents: {},
    dependencies: {},
  };
}

/**
 * The handler classes among the files' exports.
 * Throws on a file that exports none, and on a step type declared twice.
 */
export function collectHandlers(
  files: Record<string, Record<string, unknown>>,
): VersionedHandler[] {
  const handlers = new Map<string, VersionedHandler>();
  for (const [file, exports] of Object.entries(files)) {
    const found = Object.values(exports).filter(isVersionedHandler);
    if (found.length === 0) {
      throw new Error(
        `${file} exports no handler with a static type and version`,
      );
    }
    for (const handler of found) {
      const declared = handlers.get(handler.type);
      if (declared && declared !== handler) {
        throw new Error(
          `${file} declares ${handler.type}, already declared by another handler`,
        );
      }
      handlers.set(handler.type, handler);
    }
  }
  return [...handlers.values()];
}

function isVersionedHandler(value: unknown): value is VersionedHandler {
  return (
    typeof value === 'function' &&
    typeof (value as Partial<VersionedHandler>).type === 'string' &&
    typeof (value as Partial<VersionedHandler>).version === 'string'
  );
}

function versionsOf(
  handlers: readonly VersionedHandler[],
): Record<string, string> {
  const versions: Record<string, string> = {};
  for (const { type, version } of handlers) {
    versions[type] = versions[type]
      ? `${versions[type]} || ${version}`
      : version;
  }
  return versions;
}

export interface RequirementsItem {
  id: string;
  type?: string;
  clientRequirements?: ClientRequirements | null;
}

/** A payment method as the configuration sends it, as far as client requirements go. */
export interface BackendDrivenConfig extends RequirementsItem {
  implementationType?: string;
  capabilities?: readonly string[];
}

/** `WEB_REDIRECT` with a `BACKEND_DRIVEN` capability is the legacy form older manifests still send. */
export function isBackendDriven({
  implementationType,
  capabilities,
}: Pick<BackendDrivenConfig, 'implementationType' | 'capabilities'>): boolean {
  return (
    implementationType === 'BACKEND_DRIVEN' ||
    (implementationType === 'WEB_REDIRECT' &&
      capabilities?.includes('BACKEND_DRIVEN') === true)
  );
}

/**
 * What an SDK knows of its checkout: the context its client requirements are
 * checked against and its flows run in.
 */
export interface CheckoutFacts {
  sdkVersion: string;
  integrationType: string;
  checkoutSessionId: string;
  clientSessionId: string;
  customerId?: string;
  primerAccountId: string;
  /**
   * Where a redirect brings the shopper back, the SDK's own default included;
   * absent when there is nowhere to return.
   */
  returnUrl?: string;
  forceRedirect?: boolean;
}

/** The checkout's context; a flow adds `payment` to it. */
export function sdkContextFrom(facts: CheckoutFacts): SDKContext {
  const browserInfo = getBrowserInfo();
  return {
    sdk: {
      type: 'WEB',
      version: facts.sdkVersion,
      integrationType: facts.integrationType,
    },
    device: {
      platform: isMobile() ? 'MOBILE_WEB' : 'WEB',
      locale: browserInfo.language,
      userAgent: browserInfo.userAgent,
      inAppBrowser: isInAppBrowser(),
    },
    session: {
      checkoutSessionId: facts.checkoutSessionId,
      clientSessionId: facts.clientSessionId,
      customerId: facts.customerId,
    },
    merchant: {
      primerAccountId: facts.primerAccountId,
    },
    ...(facts.returnUrl && {
      redirect: {
        returnUrl: facts.returnUrl,
        ...(facts.forceRedirect !== undefined && {
          forceRedirect: facts.forceRedirect,
        }),
      },
    }),
  };
}

/** One that sends no requirements is a legacy redirect flow, as the state processor assumes. */
export function usesUrlOpen({ clientRequirements }: RequirementsItem): boolean {
  return (
    clientRequirements == null ||
    clientRequirements.stepTypes?.['url.open'] != null
  );
}

/** Each item's verdict, by id. */
export type ClientRequirementsVerdicts = Map<string, ClientRequirementsCheck>;

/** Checks every item against what this SDK declares and knows. */
export async function settleClientRequirements(
  stateProcessor: StateProcessorFactory,
  items: readonly RequirementsItem[],
  client: Client,
): Promise<ClientRequirementsVerdicts> {
  const startedAt = Date.now();
  PrimerJSLogger.info(
    'BDC: checking client requirements for',
    Object.fromEntries(
      items.map(({ id, type, clientRequirements }) => [
        type ? `${type} (${id})` : id,
        clientRequirements ?? 'none sent: legacy defaults apply',
      ]),
    ),
  );

  PrimerJSLogger.debug('BDC: client', client);
  const entries = await Promise.all(
    items.map(
      async (item) =>
        [
          item.id,
          await stateProcessor.checkClientRequirements(
            item.clientRequirements,
            client,
          ),
        ] as const,
    ),
  );

  PrimerJSLogger.info(
    `BDC: client requirements settled in ${Date.now() - startedAt}ms`,
    Object.fromEntries(
      entries.map(([id, { satisfied, unmet }]) => [
        id,
        satisfied ? 'available' : unmet,
      ]),
    ),
  );
  return new Map(entries);
}

// One run per list of payment methods, shared by every backend-driven method's
// check; a failed run is dropped so the next caller retries.
const settled = new WeakMap<
  readonly BackendDrivenConfig[],
  Promise<ClientRequirementsVerdicts>
>();

/**
 * The verdicts of the backend-driven methods among `paymentMethods`, settled
 * once for the list: the state processor loads, and each method is checked
 * against the client that `client()` builds.
 */
export function settleBackendDrivenMethods(
  paymentMethods: readonly BackendDrivenConfig[],
  stateProcessorConfig: StateProcessorConfig,
  client: () => Client,
): Promise<ClientRequirementsVerdicts> {
  let pending = settled.get(paymentMethods);
  if (!pending) {
    pending = loadStateProcessorModule(stateProcessorConfig).then(
      (stateProcessor) =>
        settleClientRequirements(
          stateProcessor,
          paymentMethods
            .filter(isBackendDriven)
            .map(({ id, type, clientRequirements }) => ({
              id,
              type,
              clientRequirements,
            })),
          client(),
        ),
    );
    settled.set(paymentMethods, pending);
    pending.catch(() => settled.delete(paymentMethods));
  }
  return pending;
}

/**
 * The payment method list waits on every method's check, so a verdict that
 * stalls hides the method rather than holding up the rest.
 */
export const CLIENT_REQUIREMENTS_TIMEOUT_MS = 5_000;

/** Whether a backend-driven method can be offered in this checkout, and why not. */
export type Availability =
  | { available: true }
  | { available: false; reason: 'UNSETTLED'; error: unknown }
  | { available: false; reason: 'NOT_CHECKED' }
  | { available: false; reason: 'UNMET'; unmet: UnmetClientRequirement[] };

/** The method's verdict, if the verdicts settle within CLIENT_REQUIREMENTS_TIMEOUT_MS. */
export async function availabilityOf(
  id: string,
  verdicts: Promise<ClientRequirementsVerdicts>,
): Promise<Availability> {
  let settledVerdicts: ClientRequirementsVerdicts;
  try {
    settledVerdicts = await Promise.race([
      verdicts,
      delay(CLIENT_REQUIREMENTS_TIMEOUT_MS).then(() => {
        throw new Error(
          `Client requirements were not settled within ${CLIENT_REQUIREMENTS_TIMEOUT_MS}ms`,
        );
      }),
    ]);
  } catch (error) {
    return { available: false, reason: 'UNSETTLED', error };
  }
  const verdict = settledVerdicts.get(id);
  if (!verdict) {
    return { available: false, reason: 'NOT_CHECKED' };
  }
  return verdict.satisfied
    ? { available: true }
    : { available: false, reason: 'UNMET', unmet: verdict.unmet };
}
